import { useScrollEdges } from '../hooks/useScrollEdges';
import React, { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from './Icon';
import { useRole } from '../hooks/useRole';
import { roleLevel } from '../utils/roles';
import { getAdminPathForView } from '../utils/adminNavigation';
import {
    type MenuItem,
    PLANNING_NAV_ITEMS,
    RELATIONS_NAV_ITEMS,
    VENDORS_NAV_ITEMS,
    STYLE_NAV_ITEMS,
    LOGISTICS_NAV_ITEMS,
    FINANCE_NAV_ITEMS,
    DAY_MANAGEMENT_NAV_ITEMS,
    ADMIN_NAV_ITEMS,
} from '../constants/sidebarNavGroups';
import { SettingsContext } from '../contexts/SettingsContext';
import { isNavFlagEnabled, isNavHidden, sanitizeHiddenMenu } from '../utils/sidebarMenu';
import '../styles/components/Sidebar.css';

export type { MenuItem };

interface SidebarProps {
    activeView: string;
    onViewChange: (view: string) => void;
    collapsed?: boolean;
    onCollapsedChange?: (collapsed: boolean) => void;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

const COLLAPSED_SECTIONS_KEY = 'anojeje_sidebar_collapsed_sections';

const Sidebar: React.FC<SidebarProps> = ({ activeView, onViewChange, collapsed = false, onCollapsedChange, mobileOpen = false, onMobileClose }) => {
    const { role, canViewFinancials } = useRole();
    const userLevel = roleLevel(role);
    // The user's own Settings > Menu choice. No provider (some tests) means nothing is hidden.
    const hiddenMenu = sanitizeHiddenMenu(useContext(SettingsContext)?.settings.menu?.hidden);

    // Below 768px the sidebar becomes an off-canvas drawer (see Sidebar.css),
    // which uses its own full-width layout regardless of the desktop collapse
    // preference. Without this, a persisted collapsed=true combined with the
    // media query's width override produced a wide sidebar with no labels —
    // React had already omitted the label spans for the (still-true) collapsed
    // prop, while CSS forced the container back to full width.
    const [isNarrowViewport, setIsNarrowViewport] = React.useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
    );
    React.useEffect(() => {
        const mq = window.matchMedia('(max-width: 768px)');
        const onChange = () => setIsNarrowViewport(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    const effectiveCollapsed = collapsed && !isNarrowViewport;
    const { ref: navRef, edges: navEdges } = useScrollEdges<HTMLElement>();

    function isItemAccessible(item: MenuItem): boolean {
        if (!item.minRole) return true;
        return userLevel >= roleLevel(item.minRole);
    }

    // Money items (Budget, Budget Variances, Quotes, Payments and its
    // children) stay hidden from a role that may not see money, same as
    // before this port — this is on top of (never instead of) the flag and
    // role-level checks above.
    const FINANCIAL_ITEM_IDS = new Set(['budget', 'budget-variances', 'quotes', 'payments']);
    function isFinancialItemVisible(item: MenuItem): boolean {
        return !FINANCIAL_ITEM_IDS.has(item.id) || canViewFinancials;
    }

    function isItemVisible(item: MenuItem): boolean {
        return isNavFlagEnabled(item.id) && isFinancialItemVisible(item) && !isNavHidden(item.id, hiddenMenu);
    }

    const SECTION_ITEMS: Record<string, MenuItem[]> = {
        'Planning': PLANNING_NAV_ITEMS,
        'Relations': RELATIONS_NAV_ITEMS,
        'Vendors': VENDORS_NAV_ITEMS,
        'Style': STYLE_NAV_ITEMS,
        'Logistics': LOGISTICS_NAV_ITEMS,
        'Finance': FINANCE_NAV_ITEMS,
        'Day Management': DAY_MANAGEMENT_NAV_ITEMS,
    };

    // Per-section expand/collapse, independent of the whole-sidebar
    // icon-only `collapsed` prop above — matches Kalligo's collapsible nav
    // groups. Persisted so a user's own toggling survives a reload; but with
    // no saved preference yet (first visit), default to collapsing every
    // section except the one containing the current page — matching
    // Kalligo's own default (only the active section starts open) instead
    // of dumping every item open regardless of where the user actually is.
    const [collapsedSections, setCollapsedSections] = React.useState<Set<string>>(() => {
        try {
            const saved = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
            if (saved) return new Set(JSON.parse(saved));
        } catch { /* ignore */ }
        const activeSection = Object.entries(SECTION_ITEMS).find(([, items]) => items.some(i => i.id === activeView))?.[0];
        return new Set(Object.keys(SECTION_ITEMS).filter(title => title !== activeSection));
    });

    function toggleSection(title: string) {
        setCollapsedSections(prev => {
            const next = new Set(prev);
            if (next.has(title)) next.delete(title); else next.add(title);
            try { localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
            return next;
        });
    }

    function handleNavClick(id: string) {
        onViewChange(id);
        onMobileClose?.();
    }

    // A child's own view id (e.g. 'logistics-vehicles') never appears in the
    // coarse `activeView` App.tsx computes (that only tracks 'logistics') —
    // so its highlighted/active state is read off the real URL instead, via
    // the same getAdminPathForView mapping handleNavClick's navigation uses.
    const location = useLocation();
    function isChildActive(item: MenuItem): boolean {
        return location.pathname === getAdminPathForView(item.id);
    }

    function renderNavButton(item: MenuItem, isActive: boolean) {
        const accessible = isItemAccessible(item);
        const blocked = item.disabled || !accessible;
        return (
            <button
                key={item.id}
                className={`nav-item ${isActive ? 'active' : ''} ${blocked ? 'disabled' : ''}`}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!blocked) handleNavClick(item.id);
                }}
                disabled={blocked}
                title={!accessible ? `Requires ${item.minRole} access` : item.label}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
            >
                <div className="nav-item-icon">
                    <Icon name={item.icon} size={18} />
                </div>
                {!effectiveCollapsed && <span className="nav-item-label">{item.label}</span>}
                {item.badge && (
                    <span className="nav-item-badge">{typeof item.badge === 'number' && item.badge > 99 ? '99+' : item.badge}</span>
                )}
            </button>
        );
    }

    // Per-item expand/collapse for nested children (Logistics/Asoebi/Favour
    // Registry/Procurement/Payments' sub-tabs). In-memory only, same as
    // Kalligo's item-level disclosure — unlike the whole-section collapse
    // above, this doesn't need to survive a reload. `undefined` means "use
    // the default (open iff it contains the active route)"; once toggled,
    // the explicit choice sticks for the tab.
    const [itemOverrides, setItemOverrides] = React.useState<Record<string, boolean>>({});
    function toggleItem(id: string, containsActive: boolean) {
        setItemOverrides(prev => ({ ...prev, [id]: !(prev[id] ?? containsActive) }));
    }

    function renderItemOrGroup(item: MenuItem) {
        const children = (item.children ?? []).filter(isItemVisible);
        // Icon-rail mode has no room for nested disclosure chrome — same
        // simplification the whole-section collapse already applies.
        if (children.length === 0 || effectiveCollapsed) {
            return renderNavButton(item, activeView === item.id);
        }
        const containsActive = children.some(isChildActive);
        const open = itemOverrides[item.id] ?? containsActive;
        return (
            <div className={`nav-item-group ${open ? 'is-open' : ''}`} key={item.id}>
                <div className="nav-item-row">
                    {renderNavButton(item, activeView === item.id && !containsActive)}
                    <button
                        type="button"
                        className="nav-item-expand-toggle"
                        aria-expanded={open}
                        aria-label={open ? `Collapse ${item.label}` : `Expand ${item.label}`}
                        onClick={() => toggleItem(item.id, containsActive)}
                    >
                        <Icon name={open ? 'chevronDown' : 'chevronRight'} size={14} />
                    </button>
                </div>
                {open && (
                    <div className="nav-items nav-items--nested">
                        {children.map(child => renderNavButton(child, isChildActive(child)))}
                    </div>
                )}
            </div>
        );
    }

    function renderNavSection(title: string, items: MenuItem[]) {
        const visible = items.filter(isItemVisible);
        if (visible.length === 0) return null;
        const sectionCollapsed = collapsedSections.has(title);
        // Icon-only sidebar mode always shows every item (no room for
        // section headers to click) — per-section collapse only applies
        // once the sidebar is fully expanded.
        const itemsVisible = effectiveCollapsed || !sectionCollapsed;
        return (
            <div className="nav-section">
                {!effectiveCollapsed && (
                    <button
                        type="button"
                        className="nav-section-title"
                        onClick={() => toggleSection(title)}
                        aria-expanded={!sectionCollapsed}
                    >
                        <span>{title}</span>
                        <span className="nav-section-title-rule" />
                        <Icon name={sectionCollapsed ? 'chevronRight' : 'chevronDown'} size={12} />
                    </button>
                )}
                {itemsVisible && (
                <div className="nav-items">
                    {visible.map(renderItemOrGroup)}
                </div>
                )}
            </div>
        );
    }

    return (
        <aside className={`sidebar${effectiveCollapsed ? ' sidebar--collapsed' : ''}${mobileOpen ? ' sidebar--mobile-open' : ''}`}>
            {/* Header */}
            <div className="sidebar-header">
                <div className="sidebar-logo">
                    <button
                        className="logo-icon"
                        onClick={() => onCollapsedChange?.(!effectiveCollapsed)}
                        title={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        aria-label={effectiveCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <Icon name="ring" size={24} />
                    </button>
                    {!effectiveCollapsed && <span className="logo-text">ĀNƆ̄ JĒJĒ</span>}
                </div>
                {!effectiveCollapsed && (
                    <button
                        className="sidebar-collapse-btn"
                        onClick={() => onCollapsedChange?.(true)}
                        title="Collapse sidebar"
                        aria-label="Collapse sidebar"
                    >
                        <Icon name="chevronLeft" size={16} />
                    </button>
                )}
            </div>

            {/* Back to the account-level workspace shell (Projects/Pipeline) —
                matches Kalligo: a project's own sidebar has no Projects/
                Pipeline items, only this one way back up. */}
            {!effectiveCollapsed && (
                <button
                    type="button"
                    className="sidebar-back-to-workspace"
                    onClick={() => onViewChange('projects')}
                >
                    <Icon name="arrowLeft" size={16} />
                    Back to workspace
                </button>
            )}

            {/* Navigation */}
            {/* The menu is taller than its box on most screens: the nav is the only scroll
                container, and the fades (decorative, never focusable) show which side still
                has items. Back to workspace above and the footer below stay put. */}
            <div className="sidebar-nav-wrap">
                <div className={`sidebar-nav-fade sidebar-nav-fade--top${navEdges.top ? ' is-visible' : ''}`} aria-hidden="true" />
                <nav ref={navRef} className="sidebar-nav" aria-label="Project navigation">
                    {renderNavSection('Planning', PLANNING_NAV_ITEMS)}
                    {renderNavSection('Relations', RELATIONS_NAV_ITEMS)}
                    {renderNavSection('Vendors', VENDORS_NAV_ITEMS)}
                    {renderNavSection('Style', STYLE_NAV_ITEMS)}
                    {renderNavSection('Logistics', LOGISTICS_NAV_ITEMS)}
                    {renderNavSection('Finance', FINANCE_NAV_ITEMS)}
                    {renderNavSection('Day Management', DAY_MANAGEMENT_NAV_ITEMS)}
                </nav>
                <div className={`sidebar-nav-fade sidebar-nav-fade--bottom${navEdges.bottom ? ' is-visible' : ''}`} aria-hidden="true" />
            </div>

            {/* Footer */}
            <div className="sidebar-footer" role="group" aria-label="Project settings and history">
                <div className="nav-items">
                    {ADMIN_NAV_ITEMS.map((item) => {
                        const accessible = isItemAccessible(item);
                        const blocked = item.disabled || !accessible;
                        return (
                            <button
                                key={item.id}
                                className={`nav-item ${activeView === item.id ? 'active' : ''} ${blocked ? 'disabled' : ''}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!blocked) handleNavClick(item.id);
                                }}
                                disabled={blocked}
                                title={blocked ? (!accessible ? `Requires ${item.minRole} access` : 'Coming soon') : item.label}
                                aria-label={item.label}
                                aria-current={activeView === item.id ? 'page' : undefined}
                            >
                                <div className="nav-item-icon">
                                    <Icon name={item.icon} size={18} />
                                </div>
                                {!effectiveCollapsed && <span className="nav-item-label">{item.label}</span>}
                                {item.badge && (
                                    <span className="nav-item-badge">{item.badge}</span>
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>
        </aside>
    );
};

export default Sidebar;
